import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import SimilarCaseIcon from '../img/logo_no_bg.png';
import ArrowDownIcon from '../img/arrow_down_20x20.png';

const CaseSummarySection = ({ summaryPoints }) => {
  const [expanded, setExpanded] = useState(false);
  const [summaryText, setSummaryText] = useState('');

  // 마크다운을 HTML로 변환하고 DOMPurify로 새니타이징하는 함수
  const convertMarkdownToHtml = (text) => {
    if (!text) return '';
    
    let convertedText = text;
    
    // **볼드체** → <strong>볼드체</strong>
    convertedText = convertedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // 줄바꿈 처리
    convertedText = convertedText.replace(/\n/g, '<br />');
    
    // DOMPurify로 새니타이징 (XSS 공격 방지)
    return DOMPurify.sanitize(convertedText, {
      ALLOWED_TAGS: ['strong', 'br'], // 허용할 HTML 태그만 지정
      ALLOWED_ATTR: [] // 허용할 속성 없음
    });
  };

  useEffect(() => {
    if (summaryPoints) {
      try {
        // summaryPoints가 문자열로 된 JSON 배열이므로 파싱
        const points = JSON.parse(summaryPoints);
        // 각 포인트 앞에 점(•) 추가
        const formattedText = points.map(point => `• ${point}`).join('\n');
        setSummaryText(formattedText);
      } catch (error) {
        console.error('Error parsing summaryPoints:', error);
        setSummaryText('');
      }
    }
  }, [summaryPoints]);

  return (
    <Wrapper>
      <Container>
        <Header>
          <TextGroup>
            <TopRow>
              <Icon src={SimilarCaseIcon} alt="아이콘" />
              <Title>유사 판례 특징 요약</Title>
            </TopRow>
            <SubTitle>내 상황과 가장 유사한 상위 5개 판례의 특징을 요약하면 다음과 같아요.</SubTitle>
          </TextGroup>
        </Header>

        <SummaryBox>
          <ScrollArea expanded={expanded}>
            <SummaryText dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(summaryText) }} />
          </ScrollArea>

          {!expanded && summaryText && (
            <ExpandButton onClick={() => setExpanded(true)}>
              더 펼쳐보기
              <Arrow src={ArrowDownIcon} alt="펼쳐보기 아이콘" />
            </ExpandButton>
          )}
        </SummaryBox>
      </Container>
    </Wrapper>
  );
};

export default CaseSummarySection;

const Wrapper = styled.div`
  width: 680px;
  background: #f9f9f9;
  margin: 0 auto;
  border-radius: 24px;

  @media (max-width: 744px) and (min-width: 481px) {
    width: calc(100% - 64px);
  }

  @media (max-width: 480px) and (min-width: 320px) {
    width: 100%;
  }
  
`;

const Container = styled.div`
  padding: 24px 32px;

  @media (max-width: 1140px) and (min-width: 745px) {
    padding: 24px 32px 32px;
  }

  @media (max-width: 744px) and (min-width: 481px) {
    padding: 18px 24px 24px;
  }

  @media (max-width: 480px) and (min-width: 320px) {
    padding: 18px 18px 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.60);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; 
  letter-spacing: -0.32px;

   @media (max-width: 744px) { 
    margin-bottom: 16px;
    align-items: flex-start;
    flex-direction: row;
  }
   
`;

const Icon = styled.img`
  width: 23.78px;
  height: 11.909px;
  flex-shrink: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;

  @media (max-width: 744px) {
   gap: 6.22px;
}
`;

const TextGroup = styled.div`
display: flex;
gap: 8px;

@media (max-width: 744px) {
  flex-direction: column;
}
`;

const Title = styled.div`
  color: rgba(0, 0, 0, 0.60);
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px; 
  letter-spacing: -0.32px;
`;

const SubTitle = styled.span`
  color: rgba(0, 0, 0, 0.30);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px; 
  letter-spacing: -0.28px;

  @media (max-width: 744px) {
    font-size: 13px;
    line-height: 20px; 
    letter-spacing: -0.26px;
    width: 229px;
  }
`;

const SummaryBox = styled.div`
  width: 616px;
  padding: 24px 16px;
  position: relative;
  border-radius: 16px;
  border: 1.5px solid transparent;

  /* 그라데이션 테두리 설정 부분 시작 : 배경 2겹 처리로 테두리 효과 구현 */
  background-image:
    linear-gradient(#fff, #fff),
    linear-gradient(90deg, #1AE276 33.51%, #3A7DFF 86.88%);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-sizing: border-box;
  /* 테두리 설정 끝 */

  @media (max-width: 1140px) and (min-width: 745px) {
    width: auto;
  }

  @media (max-width: 744px) and (min-width: 481px) {
    width: auto;
    padding: 18px 24px 18px 11px;
  }

  @media (max-width: 480px) and (min-width: 320px) {
    width: auto;
    padding: 18px 18px 18px 8px;
  }
`;

const ScrollArea = styled.div`
  max-height: ${({ expanded }) => (expanded ? 'none' : '156px')};
  overflow: hidden;
  transition: max-height 0.3s ease;

  @media (max-width: 744px) {
    max-height: ${({ expanded }) => (expanded ? 'none' : '204px')};
  }
`;

const SummaryText = styled.div`
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px; 
  letter-spacing: -0.28px;
  white-space: pre-wrap;

  /* 볼드체 스타일 */
  strong {
    font-weight: 700;
    color: rgba(0, 0, 0, 0.90);
  }

  @media (max-width: 744px) {
    color: #000;
    line-height: 24px;
  } 
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 568px;
  height: 51px;
  margin: 24px auto 0;
  padding: 12px 241px;
  border: 1px solid rgba(200, 250, 220, 0.4);
  border-radius: 12px;
  color: rgba(0, 0, 0, 0.50);
  font-size: 14px;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: -0.28px;
  cursor: pointer;

  /* 테두리 설정 시작 */
  background-image:
    linear-gradient(#fff, #fff), /* 내부 배경 */
    linear-gradient(90deg, #1AE276 33.51%, #3A7DFF 86.88%); /* 테두리용 그라데이션 */
  background-origin: border-box;
  background-clip: padding-box, border-box;
  /* 테두리 설정 부분 끝 */

  transition: background 0.2s ease;

  &:hover {
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)),
      linear-gradient(90deg, #1AE276 33.51%, #3A7DFF 86.88%);
  }

  @media (max-width: 744px) {
    width: calc(100% - 64px);
    height: 42px;
    padding: 0;
    font-size: 12px;
    margin: 24px auto 0;
  }

  @media (max-width: 480px) and (min-width: 320px) {
    width: calc(100% - 36px);
    margin: 18px auto 0;
  }
`;

const Arrow = styled.img`
  width: 20px;
  height: 20px;
`;
